import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSim } from "@/context/SimContext";
import { Card, SectionTitle } from "@/components/ui-kit";
import { ListChecks, Cpu, UserCheck, Pencil, XCircle, Gauge, Clock } from "lucide-react";

function fmtUptime(s) {
  if (s == null) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function Analytics() {
  const { eventTick } = useSim();
  const [a, setA] = useState(null);

  useEffect(() => {
    const load = async () => { try { const { data } = await api.get("/analytics"); setA(data); } catch {} };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [eventTick]);

  const cards = [
    { label: "Total Tasks", value: a?.total_tasks ?? 0, icon: ListChecks, tone: "text-slate-100" },
    { label: "Automatic Decisions", value: a?.automatic_decisions ?? 0, icon: Cpu, tone: "text-emerald-400" },
    { label: "Human Interventions", value: a?.human_interventions ?? 0, icon: UserCheck, tone: "text-blue-400" },
    { label: "Human Corrections", value: a?.corrections ?? 0, icon: Pencil, tone: "text-amber-400" },
    { label: "Rejected Actions", value: a?.rejected ?? 0, icon: XCircle, tone: "text-red-400" },
    { label: "Avg AI Confidence", value: `${a?.avg_confidence ?? 0}%`, icon: Gauge, tone: "text-emerald-400" },
    { label: "System Uptime", value: fmtUptime(a?.uptime_seconds), icon: Clock, tone: "text-slate-100" },
  ];

  const total = a?.total_tasks || 0;
  const autoPct = total ? Math.round((a.automatic_decisions / total) * 100) : 0;
  const humanPct = total ? Math.round((a.human_interventions / total) * 100) : 0;

  return (
    <div className="space-y-6 ngx-fade-in">
      <SectionTitle sub="Lightweight operational metrics from the simulated cell.">Analytics</SectionTitle>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-grid">
        {cards.map((c) => (
          <Card key={c.label} className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{c.label}</span>
              <c.icon className="h-4 w-4 text-slate-500" />
            </div>
            <div className={`font-mono text-3xl font-medium mt-3 ${c.tone}`}>{c.value}</div>
          </Card>
        ))}
      </div>

      <Card className="p-6">
        <h3 className="font-display font-semibold text-slate-200 mb-4">Autonomy vs Human Intervention</h3>
        <div className="space-y-4">
          <Bar label="Automatic Decisions" pct={autoPct} tone="bg-emerald-500" />
          <Bar label="Human Interventions" pct={humanPct} tone="bg-blue-500" />
        </div>
        <p className="text-xs text-slate-500 mt-5">
          Metrics reflect only simulated activity. They do not imply capabilities beyond the current simulator.
        </p>
      </Card>
    </div>
  );
}

function Bar({ label, pct, tone }) {
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-slate-300">{label}</span>
        <span className="font-mono text-slate-400">{pct}%</span>
      </div>
      <div className="h-3 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
